import { createContext, useContext, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { request } from '../shared/api/request';
import { getProductPrice } from '../entities/product/helpers';

const StoreContext = createContext(null);
const reportedResourceFailures = new Set();

function reportResourceFailure(resource, error) {
  if (reportedResourceFailures.has(resource)) return;
  reportedResourceFailures.add(resource);
  console.error(`Could not fetch ${resource} from backend:`, error.message);
}

export function StoreProvider({ children }) {
  /* ─── Core UI & State ─── */
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('technomax_theme');
    if (savedTheme === 'dark' || savedTheme === 'light') return savedTheme;
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });
  const [page, setPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return ['category', 'deals', 'favorites', 'search', 'sort', 'min', 'max', 'brand']
      .some((key) => params.has(key)) ? 'catalog' : 'home';
  });
  const [adminTab, setAdminTab] = useState('overview');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [settings, setSettings] = useState(null);
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem('technomax_guest_cart') || '[]'));
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [category, setCategory] = useState('');
  const [dealsOnly, setDealsOnly] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort] = useState('newest');
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authError, setAuthError] = useState('');
  const intendedActionRef = useRef(null);
  const [notice, setNoticeState] = useState('');
  const [noticeVersion, setNoticeVersion] = useState(0);
  const setNotice = useCallback((message) => {
    setNoticeState(message);
    setNoticeVersion((version) => version + 1);
  }, []);
  const [loading, setLoading] = useState(true);
  const [resourceStatus, setResourceStatus] = useState({
    products: 'loading', categories: 'loading', settings: 'loading', banners: 'loading',
  });
  const [resourceErrors, setResourceErrors] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [orderComplete, setOrderComplete] = useState(null);
  const [orders, setOrders] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /* ─── Admin state ─── */
  const [adminOrders, setAdminOrders] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminBanners, setAdminBanners] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);

  /* ─── Theme Persistence ─── */
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('technomax_theme', theme);
  }, [theme]);

  /* ─── Data fetchers ─── */
  const fetchProducts = useCallback(async ({ forceRefresh = false } = {}) => {
    setResourceStatus((current) => ({ ...current, products: 'loading' }));
    setResourceErrors((current) => ({ ...current, products: null }));
    try {
      const data = await request('/api/products?limit=100', { forceRefresh });
      if (!Array.isArray(data?.items)) throw new Error('Product data is missing from the server response.');
      setProducts(data.items);
      reportedResourceFailures.delete('products');
      setResourceStatus((current) => ({ ...current, products: 'ready' }));
      return data.items;
    } catch (err) {
      setResourceStatus((current) => ({ ...current, products: 'error' }));
      setResourceErrors((current) => ({ ...current, products: err.message }));
      reportResourceFailure('products', err);
      throw err;
    }
  }, []);

  const fetchCategories = useCallback(async ({ forceRefresh = false } = {}) => {
    setResourceStatus((current) => ({ ...current, categories: 'loading' }));
    setResourceErrors((current) => ({ ...current, categories: null }));
    try {
      const data = await request('/api/categories', { forceRefresh });
      if (!Array.isArray(data?.categories)) throw new Error('Category data is missing from the server response.');
      setCategories(data.categories);
      reportedResourceFailures.delete('categories');
      setResourceStatus((current) => ({ ...current, categories: 'ready' }));
      return data.categories;
    } catch (err) {
      setResourceStatus((current) => ({ ...current, categories: 'error' }));
      setResourceErrors((current) => ({ ...current, categories: err.message }));
      reportResourceFailure('categories', err);
      throw err;
    }
  }, []);

  const fetchSettings = useCallback(async ({ forceRefresh = false } = {}) => {
    setResourceStatus((current) => ({ ...current, settings: 'loading' }));
    setResourceErrors((current) => ({ ...current, settings: null }));
    try {
      const data = await request('/api/settings', { forceRefresh });
      if (!data?.settings) throw new Error('Store settings have not been configured.');
      setSettings(data.settings);
      reportedResourceFailures.delete('settings');
      setResourceStatus((current) => ({ ...current, settings: 'ready' }));
      return data.settings;
    } catch (err) {
      setResourceStatus((current) => ({ ...current, settings: 'error' }));
      setResourceErrors((current) => ({ ...current, settings: err.message }));
      reportResourceFailure('settings', err);
      throw err;
    }
  }, []);

  const fetchBanners = useCallback(async ({ forceRefresh = false } = {}) => {
    setResourceStatus((current) => ({ ...current, banners: 'loading' }));
    setResourceErrors((current) => ({ ...current, banners: null }));
    try {
      const data = await request('/api/banners', { forceRefresh });
      if (!Array.isArray(data?.banners)) throw new Error('Hero slides are missing from the server response.');
      setBanners(data.banners);
      reportedResourceFailures.delete('banners');
      setResourceStatus((current) => ({ ...current, banners: 'ready' }));
      return data.banners;
    } catch (err) {
      setResourceStatus((current) => ({ ...current, banners: 'error' }));
      setResourceErrors((current) => ({ ...current, banners: err.message }));
      reportResourceFailure('banners', err);
      throw err;
    }
  }, []);

  const retryCoreData = useCallback(() => Promise.allSettled([
    fetchProducts({ forceRefresh: true }),
    fetchCategories({ forceRefresh: true }),
    fetchSettings({ forceRefresh: true }),
    fetchBanners({ forceRefresh: true }),
  ]), [fetchProducts, fetchCategories, fetchSettings, fetchBanners]);

  /* ─── Initial bootstrap ─── */
  useEffect(() => {
    Promise.allSettled([
      fetchProducts(),
      fetchCategories(),
      fetchSettings(),
      fetchBanners(),
    ]).finally(() => setLoading(false));
  }, [fetchProducts, fetchCategories, fetchSettings, fetchBanners]);

  /* ─── Auto-login from stored token ─── */
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    request('/api/auth/me')
      .then(({ user: currentUser }) => {
        setUser(currentUser);
        return Promise.all([
          request('/api/cart'),
          request('/api/favorites'),
        ]);
      })
      .then(([{ cart: serverCart }, favoriteResponse]) => {
        setCart(serverCart?.items || []);
        setFavorites(favoriteResponse.productIds || []);
      })
      .catch(() => {
        localStorage.removeItem('token');
        setUser(null);
      });
  }, []);

  /* ─── Derived state ─── */
  const homeCopy = settings?.text?.home || {};
  const brand = typeof settings?.text?.brandName === 'string'
    ? settings.text.brandName
    : settings?.text?.brandName?.en || 'Technomax';

  const allCategories = categories;

  const displayedProducts = useMemo(() => {
    let list = [...products];
    if (category) {
      list = list.filter((p) => String(p.category || '').toLowerCase() === category.toLowerCase());
    }
    if (dealsOnly) {
      list = list.filter((p) => Number(p.salePrice) > 0 && Number(p.salePrice) < Number(p.price));
    }
    if (favoritesOnly) list = list.filter((p) => favorites.includes(String(p.id)));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        `${p.title || p.name || ''} ${p.brand || ''} ${p.category || ''} ${p.description || ''}`.toLowerCase().includes(q),
      );
    }
    if (sort === 'price_asc') {
      list.sort((a, b) => getProductPrice(a) - getProductPrice(b));
    } else if (sort === 'price_desc') {
      list.sort((a, b) => getProductPrice(b) - getProductPrice(a));
    } else if (sort === 'rating') {
      list.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
    } else if (sort === 'newest') {
      list.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    }
    return list;
  }, [products, category, dealsOnly, favoritesOnly, favorites, search, sort]);

  const cartCount = cart.reduce((sum, item) => sum + Number(item.qty || 0), 0);

  const cartItems = cart
    .map((item) => {
      const product = item.product || products.find((entry) => String(entry.id) === String(item.productId || item.id));
      return product ? { ...item, product, qty: Number(item.qty || 1) } : null;
    })
    .filter(Boolean);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + getProductPrice(item.product) * item.qty,
    0,
  );

  const visibleCategories = allCategories.slice(0, 6);

  /* ─── Navigation actions ─── */
  function go(nextPage) {
    setPage(nextPage);
    setMobileMenuOpen(false);
    setNotice('');
    if (nextPage !== 'product') setSelectedProduct(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function browseCategory(slug = '') {
    setCategory(slug);
    setDealsOnly(false);
    setFavoritesOnly(false);
    setSearch('');
    go('catalog');
  }

  function browseDeals() {
    setCategory('');
    setDealsOnly(true);
    setFavoritesOnly(false);
    setSearch('');
    go('catalog');
  }

  function browseFavorites() {
    if (!user) return openAuth('login');
    setCategory('');
    setDealsOnly(false);
    setFavoritesOnly(true);
    setSearch('');
    go('catalog');
  }

  async function openProduct(productOrId) {
    const id = typeof productOrId === 'object' ? productOrId.id : productOrId;
    // Set immediate preview
    if (typeof productOrId === 'object') {
      setSelectedProduct(productOrId);
    }
    go('product');
    // Fetch fresh details from backend
    try {
      const { product } = await request(`/api/products/${id}`);
      if (product) setSelectedProduct(product);
    } catch (error) {
      setNotice(`Could not load product details: ${error.message}`);
    }
  }

  async function toggleFavorite(product) {
    if (!user) {
      openAuth('login', async () => {
        try {
          await request(`/api/favorites/${product.id}`, { method: 'POST' });
          setFavorites((current) => current.includes(String(product.id)) ? current : [...current, String(product.id)]);
          setNotice('Saved to your favorites');
        } catch (error) {
          setNotice(`Could not update favorites: ${error.message}`);
        }
      });
      return;
    }
    const productId = String(product.id);
    const alreadySaved = favorites.includes(productId);
    try {
      if (alreadySaved) {
        await request(`/api/favorites/${productId}`, { method: 'DELETE' });
        setFavorites((current) => current.filter((id) => id !== productId));
        setNotice('Removed from favorites');
      } else {
        await request(`/api/favorites/${productId}`, { method: 'POST' });
        setFavorites((current) => current.includes(productId) ? current : [...current, productId]);
        setNotice('Saved to your favorites');
      }
    } catch (error) {
      setNotice(`Could not update favorites: ${error.message}`);
    }
  }

  /* ─── Cart actions with Backend Sync ─── */
  async function addToCart(product, qty = 1) {
    if (user) {
      try {
        const { cart: updated } = await request('/api/cart/items', {
          method: 'POST',
          body: JSON.stringify({ productId: product.id, qty }),
        });
        setCart(updated.items || []);
        setNotice('Added to bag');
        return true;
      } catch (error) {
        setNotice(error.message);
        return false;
      }
    } else {
      const current = JSON.parse(localStorage.getItem('technomax_guest_cart') || '[]');
      const existing = current.find((item) => String(item.productId) === String(product.id));
      if (existing) {
        existing.qty += qty;
      } else {
        current.push({ id: `guest-${product.id}`, productId: product.id, qty, product });
      }
      localStorage.setItem('technomax_guest_cart', JSON.stringify(current));
      setCart(current);
      setNotice('Added to bag');
      return true;
    }
  }

  async function buyNow(product, qty = 1) {
    const added = await addToCart(product, qty);
    if (added) go('checkout');
  }

  async function updateCart(item, qty) {
    if (qty <= 0) return removeFromCart(item);
    if (user) {
      try {
        const { cart: updated } = await request(`/api/cart/items/${item.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ qty }),
        });
        setCart(updated.items || []);
      } catch (error) {
        setNotice(error.message);
      }
    } else {
      const updated = cart.map((entry) => (entry.id === item.id ? { ...entry, qty } : entry));
      setCart(updated);
      localStorage.setItem('technomax_guest_cart', JSON.stringify(updated));
    }
  }

  async function removeFromCart(item) {
    if (user) {
      try {
        const { cart: updated } = await request(`/api/cart/items/${item.id}`, { method: 'DELETE' });
        setCart(updated.items || []);
        setNotice('Item removed from bag');
      } catch (error) {
        setNotice(error.message);
      }
    } else {
      const updated = cart.filter((entry) => entry.id !== item.id);
      setCart(updated);
      localStorage.setItem('technomax_guest_cart', JSON.stringify(updated));
      setNotice('Item removed from bag');
    }
  }

  async function clearUserCart() {
    if (user) {
      try {
        await request('/api/cart/clear', { method: 'POST' });
        setCart([]);
      } catch (error) {
        setNotice(error.message);
      }
    } else {
      localStorage.removeItem('technomax_guest_cart');
      setCart([]);
    }
  }

  /* ─── Auth Actions ─── */
  function openAuth(mode = 'login', intendedAction = null) {
    setAuthMode(mode);
    setAuthError('');
    intendedActionRef.current = intendedAction;
    setAuthOpen(true);
    setNotice('');
  }

  async function handleAuth(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries());
    try {
      const endpoint = authMode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const response = await request(endpoint, { method: 'POST', body: JSON.stringify(payload) });
      localStorage.setItem('token', response.token);

      // Sync guest cart to server
      const guestItems = JSON.parse(localStorage.getItem('technomax_guest_cart') || '[]');
      for (const item of guestItems) {
        await request('/api/cart/items', {
          method: 'POST',
          body: JSON.stringify({ productId: item.productId, qty: item.qty }),
        }).catch(() => null);
      }
      localStorage.removeItem('technomax_guest_cart');

      const [{ user: currentUser }, { cart: serverCart }, favoriteResponse] = await Promise.all([
        request('/api/auth/me'),
        request('/api/cart').catch(() => ({ cart: { items: [] } })),
        request('/api/favorites'),
      ]);

      setUser(currentUser);
      setCart(serverCart?.items || []);
      setFavorites(favoriteResponse.productIds || []);
      setAuthOpen(false);
      setAuthError('');
      setNotice(`Welcome back, ${currentUser.name || 'there'}!`);
      const intendedAction = intendedActionRef.current;
      intendedActionRef.current = null;
      if (intendedAction) await intendedAction(currentUser);
    } catch (error) {
      setAuthError(error.message);
    }
  }

  async function updateUserProfile(data) {
    try {
      const { user: updated } = await request('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      setUser(updated);
      setNotice('Profile updated successfully');
      return updated;
    } catch (error) {
      setNotice(error.message);
      throw error;
    }
  }

  function signOut() {
    localStorage.removeItem('token');
    setUser(null);
    setCart([]);
    setOrders([]);
    setFavorites([]);
    go('home');
    setNotice('Signed out successfully');
  }

  /* ─── Account & Orders ─── */
  const fetchMyOrders = useCallback(async () => {
    if (!user) return [];
    try {
      const { orders: userOrders } = await request('/api/orders');
      setOrders(userOrders || []);
      return userOrders || [];
    } catch (error) {
      console.warn('Orders fetch error:', error.message);
      return [];
    }
  }, [user]);

  async function showAccount() {
    if (!user) return openAuth();
    await fetchMyOrders();
    go('account');
  }

  /* ─── Checkout ─── */
  async function handleCheckout(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const fields = Object.fromEntries(form.entries());
    try {
      const { order, paymentUrl } = await request('/api/orders/checkout', {
        method: 'POST',
        body: JSON.stringify({
          paymentMethod: fields.paymentMethod,
          items: cartItems.map((item) => ({ productId: String(item.product.id), qty: item.qty })),
          shippingAddress: {
            name: fields.name,
            phone: fields.phone,
            email: fields.email,
            address: fields.address,
            city: fields.city,
            postalCode: fields.postalCode,
          },
          deliveryLocation: fields.deliveryLocation,
        }),
      });

      if (paymentUrl) {
        window.location.assign(paymentUrl);
      } else {
        setOrderComplete(order);
        setCart([]);
        localStorage.removeItem('technomax_guest_cart');
        setNotice(
          fields.paymentMethod === 'cod'
            ? 'Order placed successfully! We will deliver your package promptly.'
            : 'Order registered successfully.',
        );
        go('complete');
      }
    } catch (error) {
      setNotice(error.message);
    }
  }

  /* ─── Admin API Integrations ─── */
  const fetchAdminData = useCallback(async () => {
    if (user?.role !== 'admin') return;
    setAdminLoading(true);
    try {
      const [ordersRes, usersRes, settingsRes, productsRes, bannersRes] = await Promise.all([
        request('/api/admin/orders'),
        request('/api/admin/users'),
        request('/api/admin/settings'),
        request('/api/products?limit=100'),
        request('/api/admin/banners'),
      ]);
      setAdminOrders(ordersRes.orders || []);
      setAdminUsers(usersRes.users || []);
      if (settingsRes.settings) setSettings(settingsRes.settings);
      if (productsRes.items) setProducts(productsRes.items);
      setAdminBanners(bannersRes.banners || []);
    } catch (err) {
      setNotice(`Admin data fetch failed: ${err.message}`);
    } finally {
      setAdminLoading(false);
    }
  }, [user, setNotice]);

  async function adminCreateProduct(productData) {
    try {
      const res = await request('/api/admin/products', {
        method: 'POST',
        body: JSON.stringify(productData),
      });
      await fetchProducts();
      await fetchCategories();
      setNotice('Product created successfully');
      return res.product;
    } catch (err) {
      setNotice(`Create failed: ${err.message}`);
      throw err;
    }
  }

  async function adminUpdateProduct(id, patch) {
    try {
      const res = await request(`/api/admin/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      await fetchProducts();
      await fetchCategories();
      setNotice('Product updated successfully');
      return res.product;
    } catch (err) {
      setNotice(`Update failed: ${err.message}`);
      throw err;
    }
  }

  async function adminDeleteProduct(id) {
    try {
      await request(`/api/admin/products/${id}`, { method: 'DELETE' });
      await fetchProducts();
      await fetchCategories();
      setNotice('Product deleted successfully');
    } catch (err) {
      setNotice(`Delete failed: ${err.message}`);
      throw err;
    }
  }

  async function adminUpdateOrderStatus(orderId, status) {
    try {
      const res = await request(`/api/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setAdminOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status, updatedAt: new Date().toISOString() } : o)),
      );
      setNotice(`Order #${String(orderId).slice(-6)} marked as ${status}`);
      return res.order;
    } catch (err) {
      setNotice(`Status update failed: ${err.message}`);
      throw err;
    }
  }

  async function adminUpdateSettings(patch) {
    try {
      const res = await request('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      if (res.settings) setSettings(res.settings);
      setNotice('Store settings saved successfully');
      return res.settings;
    } catch (err) {
      setNotice(`Settings update failed: ${err.message}`);
      throw err;
    }
  }

  const fetchAdminBanners = useCallback(async () => {
    const response = await request('/api/admin/banners', { forceRefresh: true });
    setAdminBanners(response.banners || []);
    return response.banners || [];
  }, []);

  async function adminCreateBanner(banner) {
    const { banner: created } = await request('/api/admin/banners', {
      method: 'POST',
      body: JSON.stringify(banner),
    });
    await Promise.all([fetchAdminBanners(), fetchBanners({ forceRefresh: true })]);
    return created;
  }

  async function adminUpdateBanner(id, patch) {
    const { banner: updated } = await request(`/api/admin/banners/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    await Promise.all([fetchAdminBanners(), fetchBanners({ forceRefresh: true })]);
    return updated;
  }

  async function adminDeleteBanner(id) {
    await request(`/api/admin/banners/${id}`, { method: 'DELETE' });
    await Promise.all([fetchAdminBanners(), fetchBanners({ forceRefresh: true })]);
  }

  async function adminUploadImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await request('/api/admin/upload', {
            method: 'POST',
            body: JSON.stringify({
              fileName: file.name,
              base64: reader.result,
            }),
          });
          resolve(res.url || res.relativePath);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  }

  /* ─── Context Value ─── */
  const value = {
    // State
    theme, page, adminTab, products, categories: allCategories, settings, user, cart, selectedProduct,
    category, dealsOnly, favoritesOnly, search, searchOpen, sort, authOpen, authMode, authError,
    notice, loading, orderComplete, orders, mobileMenuOpen,
    noticeVersion,
    adminOrders, adminUsers, adminBanners, adminLoading,
    resourceStatus, resourceErrors, banners, favorites,
    // Derived
    homeCopy, brand, displayedProducts, cartCount, cartItems, subtotal,
    visibleCategories,
    // Setters
    setTheme, setSearch, setSearchOpen, setSort, setCategory, setDealsOnly, setFavoritesOnly,
    setNotice, setMobileMenuOpen, setAuthMode, setAuthOpen, setAuthError, setPage, setAdminTab,
    // Storefront Actions
    go, browseCategory, browseDeals, browseFavorites, openProduct,
    addToCart, buyNow, updateCart, removeFromCart, clearUserCart,
    openAuth, handleAuth, updateUserProfile, signOut, showAccount, handleCheckout,
    fetchProducts, fetchCategories, fetchSettings, fetchMyOrders,
    fetchBanners, retryCoreData, toggleFavorite,
    // Admin Actions
    fetchAdminData, adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
    adminUpdateOrderStatus, adminUpdateSettings, adminUploadImage,
    fetchAdminBanners, adminCreateBanner, adminUpdateBanner, adminDeleteBanner,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}
